import { type AxiosResponse } from 'axios'
import { type FormTypes } from '../features/ProjectCourseFeature/SyncSectionModal/SurveyDatesPanel'
import { type ISurveyDates } from '../models/AutoImports'
import api from '../utils/api'
import { type ISyncRules } from '../models/SyncRules'
import { type ISlot } from '../models/DistributionPlan'
import { type SlotEmailSettings } from '../models/SlotEmailSettings'

interface updateOptOutOrOptInPayload {
  params: { slotId: string, objectId: string }
  body: { isOptedOut: boolean }
}

interface InstructorOptOutEntry {
  instructorUserId: string
  isOptedOut: boolean
}

interface updateInstructorOptOutPayload {
  params: { slotId: string, sectionId: string }
  body: { instructors: InstructorOptOutEntry[] }
}

export const submitSurveyDates = async (data: { payload: Array<Partial<FormTypes>>, slotId: string }): Promise<string | any> => {
  const { payload, slotId } = data
  try {
    const response = await api.post(`/api/slots/${slotId}/default_date_settings/`, payload)
    return { data: response.data, status: response.status }
  } catch (error: any) {
    throw new Error(error.response?.data ?? 'Error submitting survey dates')
  }
}

export const getSurveyDates = async (slotId: string): Promise<ISurveyDates[]> => {
  const response = await api.get(`/api/slots/${slotId}/default_date_settings/`, {
    headers: {
      Accept: 'application/cdg'
    }
  })
  return response.data.data
}

export const getLatestSurveyDates = async (projectId: string): Promise<ISurveyDates[]> => {
  try {
    const response = await api.get(`/api/distribution_series/${projectId}/latest_default_date_settings/`)
    return response.data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return []
    }
    throw error
  }
}

export const getSectionFields = async (): Promise<string[]> => {
  const response = await api.get('/api/slots/section_fields/')
  return response.data
}

export const submitAutoImport = async ({ slotId, data }: { slotId: string, data: any }): Promise<AxiosResponse> => {
  return await api.post(`/api/slots/${slotId}/auto_import/`, data)
}

export const getAutoImport = async (slotId: string): Promise<ISyncRules> => {
  const response = await api.get(`/api/slots/${slotId}/auto_import/`)
  return response.data
}

export const getLatestAutoImport = async (projectId: string): Promise<ISyncRules | null> => {
  try {
    const response = await api.get(`/api/distribution_series/${projectId}/latest_slot_sync_rules/`)
    return response.data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null
    }
    throw error
  }
}

export const updateSlot = async (data: any): Promise<string | any> => {
  const response = await api.patch(`/api/slots/${data.slotId}`, data.payload)
  return response.data
}

export interface ApplyDefaultInstructorAddedQuestionsPayload {
  enabled: boolean
  defaultSurveyTemplateId?: string
  confirm?: boolean
  // Backend CR-270 follow-up: destructively replaces every conflicting instructor's content,
  // touched or not. Requires confirm: true in the same request — the Backend rejects a bare
  // force: true with a 400.
  force?: boolean
}

export const applyDefaultInstructorAddedQuestions = async (
  slotId: string,
  payload: ApplyDefaultInstructorAddedQuestionsPayload
): Promise<{ data: any, status: number }> => {
  const response = await api.post(
    `/api/slots/${slotId}/instructor_added_questions/apply_default/`,
    payload
  )
  return { data: response.data, status: response.status }
}

/**
 * Section-added counterpart (CR-295). Same verb, same payload shape, same response contract —
 * only the path segment differs, per the Backend's commitment of 2026-08-20.
 *
 * The `409` body differs from the instructor endpoint's: conflicts are course sections, with no
 * per-instructor dimension, so it is rendered by `ApplySectionDefaultQuestionsConflictModal`
 * rather than the instructor one. See
 * docs/components/survey-projects-and-delivery/section-added-questions.md.
 *
 * Not yet live: the Backend POC branch exists but this endpoint has not been exercised against
 * it, so treat the first real call as the contract test.
 */
export const applyDefaultSectionAddedQuestions = async (
  slotId: string,
  payload: ApplyDefaultInstructorAddedQuestionsPayload
): Promise<{ data: any, status: number }> => {
  const response = await api.post(
    `/api/slots/${slotId}/section_added_questions/apply_default/`,
    payload
  )
  return { data: response.data, status: response.status }
}

export const getSlotById = async (slotId: string): Promise<string | any> => {
  const response = await api.get(`/api/slots/${slotId}`)
  return response.data
}

export const surveyPreview = async (slotId: string, sectionId: string): Promise<string | any> => {
  const response = await api.get(`/api/slots/${slotId}/sections/${sectionId}/preview/`)
  return response.data
}

export const getAllInstructorQuestions = async (slotId: string, sectionId: string, params?: any): Promise<AxiosResponse> => {
  return await api.get(`/api/slots/${slotId}/sections/${sectionId}/template/`, { params })
}

export const postAllInstructorQuestions = async (
  slotId: string,
  sectionId: string,
  payload: any,
  params?: any
): Promise<AxiosResponse> => {
  return await api.post(
    `/api/slots/${slotId}/sections/${sectionId}/template/`,
    payload,
    { params }
  )
}

export const patchAllInstructorQuestions = async (
  slotId: string,
  sectionId: string,
  payload: any,
  params?: any
): Promise<AxiosResponse> => {
  return await api.patch(
    `/api/slots/${slotId}/sections/${sectionId}/template/`,
    payload,
    { params }
  )
}

/**
 * The course section's *shared* added-questions set (CR-295, SECTION_ADDED).
 *
 * `section_template/` rather than `template/`: one set per section, shared by every instructor on
 * it, as opposed to the per-instructor sets `getAllInstructorQuestions` returns. The response is
 * the same serialized `SurveySection` shape, so the question builder consumes it unchanged — the
 * difference is ownership, not payload.
 *
 * Returns `{}` when the section has no shared set yet. That is a normal state, not an error: the
 * builder has to tell "nothing added" apart from "section not in this project", which is the 404.
 * Readable in any project status, unlike the writes below.
 */
export const getSectionAddedQuestions = async (slotId: string, sectionId: string, params?: unknown): Promise<AxiosResponse> => {
  return await api.get(`/api/slots/${slotId}/sections/${sectionId}/section_template/`, { params })
}

/**
 * Create the section's shared set. **Published only** — 400 otherwise, and also 400 if the caller
 * is not an instructor on the section or the type is not enabled for the project.
 */
export const postSectionAddedQuestions = async (
  slotId: string,
  sectionId: string,
  payload: unknown,
  params?: unknown
): Promise<AxiosResponse> => {
  return await api.post(
    `/api/slots/${slotId}/sections/${sectionId}/section_template/`,
    payload,
    { params }
  )
}

/**
 * Edit the section's shared set. Any instructor on the section may edit, and the edit applies to
 * all of them — there is no per-instructor copy to reconcile. That is the whole feature, and it is
 * why the screen has to say so before an edit lands.
 */
export const patchSectionAddedQuestions = async (
  slotId: string,
  sectionId: string,
  payload: unknown,
  params?: unknown
): Promise<AxiosResponse> => {
  return await api.patch(
    `/api/slots/${slotId}/sections/${sectionId}/section_template/`,
    payload,
    { params }
  )
}

export const getAllAdminQuestions = async (slotId: string, divisionId: string, params?: any): Promise<AxiosResponse> => {
  return await api.get(`/api/slots/${slotId}/divisions/${divisionId}/template/`, { params })
}

export const postAllAdminQuestions = async (
  slotId: string,
  divisionId: string,
  payload: any,
  params?: any
): Promise<AxiosResponse> => {
  return await api.post(
    `/api/slots/${slotId}/divisions/${divisionId}/template/`,
    payload,
    { params }
  )
}

export const patchAllAdminQuestions = async (
  slotId: string,
  divisionId: string,
  payload: any,
  params?: any
): Promise<AxiosResponse> => {
  return await api.patch(
    `/api/slots/${slotId}/divisions/${divisionId}/template/`,
    payload,
    { params }
  )
}

export const getLatestSlotByDistributionId = async (distributionId: string): Promise<ISlot | null> => {
  try {
    const response = await api.get(`/api/distribution_series/${distributionId}/latest_slot/`)
    return response.data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null
    }
    throw new Error(error.response?.data ?? 'Error getting latest slot')
  }
}

export const getLatestSlotEmailSettings = async (distributionId: string): Promise<SlotEmailSettings[]> => {
  try {
    const response = await api.get(`/api/distribution_series/${distributionId}/latest_slot_email_settings/`)
    return response.data
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return []
    }
    throw error
  }
}

export const patchEmailSettings = async (slotId: string, payload: SlotEmailSettings[]): Promise<AxiosResponse> => {
  return await api.patch(`/api/slots/${slotId}/email_settings/`, payload)
}

export const updateOptOutOrOptIn = async (payload: updateOptOutOrOptInPayload): Promise<AxiosResponse> => {
  return await api.post(`/api/slots/${payload.params.slotId}/optout/${payload.params.objectId}`, payload.body)
}

export const updateInstructorOptOut = async (payload: updateInstructorOptOutPayload): Promise<AxiosResponse> => {
  return await api.post(`/api/slots/${payload.params.slotId}/sections/${payload.params.sectionId}/instructor_optout`, payload.body)
}

export default {
  submitSurveyDates,
  getSurveyDates,
  getLatestSurveyDates,
  getSectionFields,
  getAutoImport,
  getLatestAutoImport,
  submitAutoImport,
  updateSlot,
  applyDefaultInstructorAddedQuestions,
  applyDefaultSectionAddedQuestions,
  getSlotById,
  surveyPreview,
  getAllInstructorQuestions,
  postAllInstructorQuestions,
  patchAllInstructorQuestions,
  getSectionAddedQuestions,
  postSectionAddedQuestions,
  patchSectionAddedQuestions,
  getAllAdminQuestions,
  postAllAdminQuestions,
  patchAllAdminQuestions,
  getLatestSlotByDistributionId,
  getLatestSlotEmailSettings,
  patchEmailSettings,
  updateOptOutOrOptIn,
  updateInstructorOptOut
}
